import { useEffect, useRef, type ReactNode } from 'react';

interface Props {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Width override (CSS value). Defaults to min(380px, 94vw). */
  width?: string;
}

/**
 * A slide-in panel from the right edge — used for quick editing tasks
 * where a full modal is too heavy (assigning reps to an area, etc.).
 *
 * Reuses the .drawer-* classes already in styles.css.
 */
export default function Drawer({
  title, subtitle, onClose, children, footer, width,
}: Props) {
  const panel = useRef<HTMLElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    panel.current?.focus();

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panel}
        style={width ? { width } : undefined}
      >
        <div className="drawer-head">
          <div>
            <h3>{title}</h3>
            {subtitle ? (
              <p className="muted small" style={{ margin: '2px 0 0' }}>{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label="Close"
            style={{ fontSize: 20, lineHeight: 1 }}
          >
            &times;
          </button>
        </div>
        <div className="drawer-body">{children}</div>
        {footer ? <div className="drawer-foot">{footer}</div> : null}
      </aside>
    </div>
  );
}
